import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./styles/app.css";
import { loadConfig } from "./config";
import { initAppInsights } from "./telemetry/appInsights";
import { reportError } from "./telemetry/reportError";
import { eventBus } from "@optimyl/events";
import { AuthProvider } from "./auth/AuthProvider";
import { App } from "./App";

/**
 * Application bootstrap sequence.
 *
 * This is the entry point — like Program.cs in an Azure Function.
 * The order matters:
 *   1. Load runtime config from /config.json (environment-specific settings)
 *   2. Initialize Application Insights (so we capture startup errors)
 *   3. Wire event bus subscribers (error → telemetry + toast)
 *   4. Initialize auth (ADO-3277 — OIDC against Optimyl.Identity)
 *   5. Render the React app
 */
async function bootstrap() {
  try {
    // 1. Load runtime config
    const config = await loadConfig();

    // 2. Initialize telemetry
    initAppInsights(config.APPINSIGHTS_CONNECTION_STRING);

    // 3. Wire event bus subscribers
    eventBus.subscribe("error", reportError);

    // 4. Render with AuthProvider (oidc-client-ts)
    const rootEl = document.getElementById("root");
    if (!rootEl) throw new Error("Root element #root not found in DOM");
    const root = createRoot(rootEl);
    root.render(
      <StrictMode>
        <AuthProvider>
          <App />
        </AuthProvider>
      </StrictMode>,
    );
  } catch (err) {
    // Pre-telemetry fallback — App Insights hasn't initialized yet
    if (import.meta.env.DEV) console.error("Failed to start application:", err);
    const fallbackEl = document.getElementById("root");
    if (fallbackEl) fallbackEl.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:sans-serif">
        <div style="text-align:center">
          <h1>Unable to load application</h1>
          <p>Please try refreshing the page.</p>
        </div>
      </div>
    `;
  }
}

bootstrap();
